import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import * as _ from 'lodash';

import { createUnixNameFieldDefinition, ProjectService } from '../../../services/project.service';
import { ProjectModel } from '../../../services/project.model.service';
import { UiService } from '../../../services/core.ui.service';
import { GfTranslateService } from '../../../services/core.translate.service';
import { ServiceResponseProcessor } from '../../../services/core.response-processor.service';
import { StateService } from '@uirouter/angular';


const FIELDS_TO_SUBMIT = [
    'parentProjectId',
    'unixName',
    'name',
    'description',
    'modules',
    'invites',
    'templateProjectId',
    'isTemplate',
    'isPublic',
    'projectText6', // Used to mark orgs for future querying
    'captcha',  // maybe??
];

@Injectable()
export class ProjectRegistrationHelper {

    activeTab: String = "Project";
    savingProject = false;

    publicPrivate = { Public: 'Y', Private: 'N' };
    yesNo = { Yes: 'Y', No: 'N' };

    captchaRefresh = 0;
    isProjectRegistration = true;
    availableScmModules = ['svn', 'git'];
    showOrgs = false;
    isOrg = false;

    constructor(
        private fb: UntypedFormBuilder,
        private projectService: ProjectService,
        private projectModel: ProjectModel,
        private uiService: UiService,
        private serviceResponseProcessor: ServiceResponseProcessor,
        private gfTranslateService: GfTranslateService,
        private stateService: StateService
    ) { }

    getRegistrationForm(defaults: any = {}): UntypedFormGroup {

        let ret = this.fb.group({
            isOrgRegistration: [ false ],
            isProjectRegistration: [true],
            parentProject: [
                null,
            ],
            creatingProject: [false],
            children: [[]],
            parentProjectId: [
                0,
            ],
            unixName: createUnixNameFieldDefinition(this.projectService),
            name: [
                '',
                { 
                    validators: [
                        Validators.required,
                        Validators.maxLength(40),
                    ],
                    updateOn: 'blur',
                }
            ],
            description: ['', { updateOn: 'blur' }], // Optional field, default is an empty string
            modules: [''], // Default is an empty array
            invites: ['', { updateOn: 'blur' }], // Optional field, can be empty
            allowTemplateSelect: [
                0,
            ],
            templateProjectId: [
                0,
                [
                    Validators.required,
                    Validators.min(0), // Must be 0 or greater
                ],
            ],
            isTemplate: [
                'N',
                [
                    Validators.required,
                    Validators.pattern('^[YN]$'), // Allowed values are T or F
                ],
            ],
            isPublic: [
                'N',
                [
                    Validators.required,
                    Validators.pattern('^[YN]$'), // Allowed values are T or F
                ],
            ],
            requireCaptcha: [
                0
            ],
            captcha: [
                null,
            ],
            projectText6: [''],
        });

        const doNotTrigger = {
            emitEvent: false, // Prevent triggering valueChanges again
        };

        ret.get('unixName')?.valueChanges.subscribe((value) => {
            if (value && value !== value.toLowerCase()) {
                // Update the value to lowercase
                ret.get('unixName')?.setValue(value.toLowerCase(), doNotTrigger);
            }
        });

        ret.get('templateProjectId').valueChanges.subscribe((value) => {
            if (ret.get('isOrgRegistration')?.value) {
                // If this is an org registration, 
                // we don't need to fetch modules
                return;
            }

            if (!value) {
                return ret.get('modules')?.setValue('', doNotTrigger);
            }

            const control = ret.get('modules');
            if (! control) {
                console.log("No modules control??");
            }

            let modules = '';
            this.projectService.modules(value).then(
                (response) => {
                    modules = _.get(response, 'items', []).join(',');
                },
                (result) => {
                    console.log("fetch modules for template project failed", value, result);
                }
            ).finally(
                () => {
                    console.log("setting modules for template project", value, modules);
                    control.setValue(modules, doNotTrigger);
                }
            )
        });

        // Set default values
        defaults && ret.patchValue(defaults);

        return ret;
    }

    getSubmitData(form: UntypedFormGroup) {
        // Don't include fields that are in the form for display purposes.
        // Don't include fields that have no value.
        return FIELDS_TO_SUBMIT.reduce(
            (data: any, field: string) => {
            if (form.get(field)) {
                data[field] = form.get(field)?.value;
            }
            return data;
        }, {});
    }

    setTab(tabName) {
        this.activeTab = tabName;
    };

    setCaptchaRefresh() {
        this.captchaRefresh = new Date().getTime();
    }

    setIsOrg(project: any) {
        this.isOrg = this.isProjectAnOrg(project);
    }

    isProjectAnOrg(project) {
        return (_.get(project, 'rel.children.length', 0) > 0 ||
            _.get(project, 'projectText6', '') == 'org');
    }

    createProject(formData: any) {
        let projectData = this.getSubmitData(formData);

        // Set parentProjectId from parentProject control
        projectData.parentProjectId = formData.get('parentProject')?.value?.id || 0;

        // Preprocess the invites field
        projectData.invites = projectData.invites
            .replace(/[\r\n|\n|\r]+/, ',')
            .replace(/\s/g, '');

        formData.patchValue({
            creatingProject: true,
        });

        var promise = this.projectService.save('', projectData)
            .then(
                (response) => {
                    this.uiService.message.showSuccess(this.gfTranslateService.translate('Your new project has been successfully submitted.'), 'Project Submitted');
                    this.stateService.go('home.project.summary', {
                        unixName: projectData.unixName
                    });
                },
                (response: any) => {
                    //I had to add this line because we use 'name' in project Object but we got projectName
                    //from the api. Once api is refactored we can remove this
                    if (response.data.projectName) {
                        response.data.name = response.data.projectName;
                    }
                    this.uiService.form.showFormError(
                        this.serviceResponseProcessor,
                        this.gfTranslateService.translate('There was an error processing your project submission.'),
                        'projectRegistrationForm',
                        response.data,
                        this.projectModel
                    );

                    if (response.data.captcha) {
                        this.setCaptchaRefresh();
                        this.uiService.message.showError('The CAPTCHA you entered was incorrect', 'CAPTCHA');
                        this.setTab('Project');
                        this.projectModel.data.captcha = '';
                    }
                }
            ).finally(() => { 
                formData.patchValue({
                    creatingProject: false,
                });        
             });
        return promise;
    };    
}
